import shave from 'shave';
import { debounce } from 'lodash';
import smoothscroll from 'smoothscroll-polyfill';
import isMobile from '../../../resources/scripts/helpers/is-mobile';

class BrixModuleEvent {
  static init(...parameters) {
    return new BrixModuleEvent(...parameters);
  }

  constructor(selector) {
    this.selector = selector;

    this.calendarButton = this.selector.querySelector('.link.-calendar');
    this.calendarList = this.selector.querySelector('.list.-calendar');

    this.shareButton = this.selector.querySelector('.link.-share');
    this.shareList = this.selector.querySelector('.list.-share');

    this.setUpShare();
    this.setUpCalendar();
    this.setUpMoreOccasionsScroll();
    this.setUpForm();
  }

  setUpCalendar() {
    if (this.calendarButton && this.calendarList) {
      const onClickModule = (e) => {
        if (e.target !== this.calendarButton) {
          this.calendarButton.classList.remove('-active');
          this.calendarList.classList.remove('-show');
          this.calendarButton.setAttribute('aria-expanded', 'false');
          document.removeEventListener('click', onClickModule);
        }
      };

      // Add keyboard event listener when menu opens
      this.calendarList.addEventListener('keydown', (event) => {
        this.trapFocus(event, this.calendarList);

        // Close calendar list when ESC is pressed
        if (event.key === 'Escape' || event.keyCode === 27) {
          this.calendarButton.classList.remove('-active');
          this.calendarList.classList.remove('-show');
          this.calendarButton.setAttribute('aria-expanded', 'false');
          document.removeEventListener('click', onClickModule);
          this.calendarButton.focus(); // Return focus to the button
        }
      });

      // Set initial ARIA state
      this.calendarButton.setAttribute('aria-expanded', 'false');

      this.calendarButton.addEventListener('click', (event) => {
        event.stopPropagation();
        const isExpanded = this.calendarButton.classList.contains('-active');
        this.calendarButton.classList.toggle('-active');
        this.calendarList.classList.toggle('-show');
        this.calendarButton.setAttribute('aria-expanded', (!isExpanded).toString());

        if (this.calendarList) {
          // Set focus to first focusable element when opening
          const firstFocusable = this.calendarList.querySelector('a[href]:not([disabled]), button:not([disabled])');
          if (firstFocusable) {
            firstFocusable.focus();
          }
        }

        if (this.shareButton && this.shareList) {
          this.shareButton.classList.remove('-active');
          this.shareList.classList.remove('-show');
          this.shareButton.setAttribute('aria-expanded', 'false');
        }

        this.selector.addEventListener('click', onClickModule);
      });
    }
  }

  setUpShare() {
    this.shareLinks = this.selector.querySelectorAll('.list.-share a');
    this.shareWindow = null;

    if (this.shareButton && this.shareList) {
      const onClickModule = (e) => {
        if (e.target !== this.shareButton) {
          this.shareButton.classList.remove('-active');
          this.shareList.classList.remove('-show');
          this.shareButton.setAttribute('aria-expanded', 'false');
          document.removeEventListener('click', onClickModule);
        }
      };

      // Add keyboard event listener when menu opens
      this.shareList.addEventListener('keydown', (event) => {
        this.trapFocus(event, this.shareList);

        // Close calendar list when ESC is pressed
        if (event.key === 'Escape' || event.keyCode === 27) {
          this.shareButton.classList.remove('-active');
          this.shareList.classList.remove('-show');
          this.shareButton.setAttribute('aria-expanded', 'false');
          document.removeEventListener('click', onClickModule);
          this.shareButton.focus(); // Return focus to the button
        }
      });

      // Set initial ARIA state
      this.shareButton.setAttribute('aria-expanded', 'false');

      this.shareButton.addEventListener('click', (event) => {
        event.stopPropagation();
        const isExpanded = this.shareButton.classList.contains('-active');
        this.shareButton.classList.toggle('-active');
        this.shareList.classList.toggle('-show');
        this.shareButton.setAttribute('aria-expanded', (!isExpanded).toString());

        if (this.shareList) {
          const firstFocusable = this.shareList.querySelector('a[href]:not([disabled]), button:not([disabled])');
          if (firstFocusable) {
            firstFocusable.focus();
          }
        }

        if (this.calendarButton && this.calendarList) {
          this.calendarButton.classList.remove('-active');
          this.calendarList.classList.remove('-show');
          this.calendarButton.setAttribute('aria-expanded', 'false');
        }

        this.selector.addEventListener('click', onClickModule);
      });
    }

    if (this.shareLinks) {
      Array.from(this.shareLinks).forEach((link) => {
        link.addEventListener('click', (e) => {
          if (!isMobile()) {
            e.preventDefault();
            const shareLink = e.currentTarget.href;
            const windowOptions = 'width=600, height=500, menubar=no, centerscreen=yes';
            if (this.shareWindow === null || this.shareWindow.closed) {
              this.shareWindow = window.open(shareLink, 'shareWindow', windowOptions);
            } else {
              this.shareWindow.location = shareLink;
              this.shareWindow.focus();
            }
          }
        });
      });
    }
  }

  setUpMoreOccasionsScroll() {
    this.scrollWrapper = this.selector.querySelector('.scroll-wrapper');

    if (this.scrollWrapper) {
      this.scrollWrapper = this.selector.querySelector('.scroll-wrapper');
      this.occasionsWrapper = this.selector.querySelector('.occasions');
      this.occasions = this.selector.querySelectorAll('.occasion');
      this.firstOccasion = this.occasionsWrapper.querySelector('.occasion');
      this.scrollButtonsWrapper = this.selector.querySelector('.scroll-buttons');
      this.earlierDatesButton = this.selector.querySelector('.scroll-button.-earlier');
      this.laterDatesButton = this.selector.querySelector('.scroll-button.-later');
      this.moreEventsTitles = this.occasionsWrapper.querySelectorAll('.date .title');

      smoothscroll.polyfill();
      this.toggleScrollControls();
      this.truncateEventTitle();
      this.updateAlignment();

      window.addEventListener('resize', debounce(() => {
        this.toggleScrollControls();
        this.truncateEventTitle();
      }, 50));

      if (this.earlierDatesButton) {
        this.earlierDatesButton.addEventListener('click', () => {
          this.occasionsWrapper.scrollBy({
            top: 0,
            left: -416,
            behavior: 'smooth',
          });
        });
      }

      if (this.earlierDatesButton) {
        this.laterDatesButton.addEventListener('click', () => {
          this.occasionsWrapper.scrollBy({
            top: 0,
            left: 416,
            behavior: 'smooth',
          });
        });
      }

      this.occasionsWrapper.addEventListener('scroll', debounce(() => {
        this.toggleButtonVisibility();

        // const scrollPercentage = (100 * this.occasionsWrapper.scrollLeft)
        //   / (this.occasionsWrapper.scrollWidth - this.occasionsWrapper.clientWidth);
        // this.scrollIndicatorInner.style.left = `calc(${scrollPercentage}% - ${98 * (scrollPercentage / 100)}px`;
      }, 50));

      window.addEventListener('resize', debounce(() => {
        this.updateAlignment();
      }, 50));
    }
  }

  toggleScrollControls() {
    if (this.occasionsWrapper && this.scrollWrapper) {
      if (this.occasionsWrapper.scrollWidth > this.scrollWrapper.offsetWidth) {
        // this.scrollIndicator.classList.remove('-hide');
        this.toggleButtonVisibility();
        this.occasionsWrapper.addEventListener('scroll', this.onDateWrapperScroll());
      } else {
        this.occasionsWrapper.removeEventListener('scroll', this.onDateWrapperScroll());
        // this.scrollIndicator.classList.add('-hide');
        this.earlierDatesButton.classList.remove('-show');
        this.laterDatesButton.classList.remove('-show');
      }
    }
  }

  onDateWrapperScroll() {
    this.toggleButtonVisibility();

    // const scrollPercentage = (100 * this.occasionsWrapper.scrollLeft)
    //   / (this.occasionsWrapper.scrollWidth - this.occasionsWrapper.clientWidth);
    // this.scrollIndicatorInner.style.left = `calc(${scrollPercentage}% - ${98 * (scrollPercentage / 100)}px`;
  }

  toggleButtonVisibility() {
    this.laterDatesButton.classList.add('-show');

    if (this.occasionsWrapper.scrollLeft > 1 && !this.earlierDatesButton.classList.contains('-show')) {
      this.earlierDatesButton.classList.add('-show');
    }

    if (this.occasionsWrapper.scrollLeft < 1 && this.earlierDatesButton.classList.contains('-show')) {
      this.earlierDatesButton.classList.remove('-show');
    }

    if (this.occasionsWrapper.scrollLeft > 1 && !this.earlierDatesButton.classList.contains('-show')) {
      this.laterDatesButton.classList.add('-show');
    }

    if (this.occasionsWrapper.scrollLeft + this.occasionsWrapper.offsetWidth
      >= this.occasionsWrapper.scrollWidth && this.earlierDatesButton.classList.contains('-show')) {
      this.laterDatesButton.classList.remove('-show');
    }

    if (this.occasionsWrapper.scrollLeft + this.occasionsWrapper.offsetWidth
      <= this.occasionsWrapper.scrollWidth && !this.earlierDatesButton.classList.contains('-show')) {
      this.laterDatesButton.classList.add('-show');
    }
  }

  truncateEventTitle() {
    Array.from(this.moreEventsTitles).forEach((title) => {
      shave(title, 62);
    });
  }

  updateAlignment() {
    if (this.occasionsWrapper.clientWidth <= this.scrollWrapper.clientWidth
      && this.occasions.length * this.firstOccasion.clientWidth
      > this.occasionsWrapper.clientWidth) {
      this.occasionsWrapper.classList.add('-no-padding');
    } else {
      this.occasionsWrapper.classList.remove('-no-padding');
    }
  }

  setUpForm() {
    this.formWrapper = this.selector.querySelector('.form-wrapper');
    this.form = this.formWrapper.querySelector('.sign-up-form');
    this.fields = this.form.querySelectorAll('[id^="form-field"]');
    this.statusMessage = this.formWrapper.querySelector('.status-message');
    this.url = this.formWrapper.querySelector('input[name="url"]').value;
    this.subject = this.formWrapper.querySelector('input[name="subject"]').value;
    this.recipientAddressEncrypted = this.formWrapper.querySelector('input[name="recipientAddressEncrypted"]').value;

    this.validationMessage = this.formWrapper.querySelector('.validation-message');
    this.sendSuccessMessage = this.formWrapper.querySelector('.send-success-message');
    this.sendErrorMessage = this.formWrapper.querySelector('.send-error-message');

    let showForm = false;
    const showFormButton = this.selector.querySelector('.show-form-button');
    const formWrapperHeight = this.formWrapper.scrollHeight;

    if (this.formWrapper) {
      showFormButton.addEventListener('click', () => {
        const formWrapperStyle = this.formWrapper.style;

        if (this.formWrapper.classList.contains('-show')) {
          showForm = false;
          this.formWrapper.classList.remove('-show');
          formWrapperStyle.height = '0px';
        } else {
          showForm = true;
          this.formWrapper.classList.add('-show');
          formWrapperStyle.height = `${formWrapperHeight}px`;

          // this.form.querySelector('#form-field-first-name').focus();
        }
      });

      // TODO: This also trigger by the send button hover transition - needs fix!
      this.formWrapper.addEventListener('transitionend', (event) => {
        if (event.target !== this.formWrapper) {
          return;
        }

        if (showForm) {
          window.scrollTo({
            top: window.scrollY + this.formWrapper.getBoundingClientRect().top - document.querySelector('.menu-bar-wrapper').scrollHeight,
            left: 0,
            behavior: 'smooth',
          });
        }
      });

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.validateForm();
      });
    }
  }

  validateForm() {
    this.validationMessage.classList.remove('-show');
    this.sendSuccessMessage.classList.remove('-show');
    this.sendErrorMessage.classList.remove('-show');

    const invalid = [];

    Array.from(this.fields).forEach((field) => {
      field.classList.remove('-error');
      const errorId = `#error-${field.id}`;
      const errorMessage = this.formWrapper.querySelector(errorId);

      if (field.hasAttribute('required')) {
        errorMessage.classList.remove('-show');
        if (!field.validity.valid) {
          invalid.push(field);
          field.classList.add('-error');
          errorMessage.classList.add('-show');
        }
      }
    });

    if (invalid.length > 0) {
      this.validationMessage.classList.add('-show');
      if (!isMobile) {
        invalid[0].focus();
      }
    } else {
      this.sendForm();
      // this.form.submit();
    }
  }

  sendForm() {
    const formData = new FormData();

    Array.from(this.fields).forEach((field) => {
      formData.append(field.name.split(' ').join('_'), field.value);
    });

    formData.append('recipientAddressEncrypted', this.recipientAddressEncrypted);
    formData.append('subject', this.subject);

    const fetchOptions = {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
    };

    const responsePromise = fetch(this.url, fetchOptions);

    responsePromise.then((response) => {
      if (response.status === 200) {
        Array.from(this.fields).forEach((field) => {
          field.value = ''; // eslint-disable-line no-param-reassign
        });

        this.sendSuccessMessage.classList.add('-show');
      } else {
        this.sendErrorMessage.classList.add('-show');
      }
    });
  }

  trapFocus(event, element) {
    const focusableElements = element.querySelectorAll(
      'a[href]:not([disabled]), button:not([disabled])',
    );
    const hamburgerButton = this.toggleMenuButton;
    const firstElement = hamburgerButton || focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const isTabPressed = event.key === 'Tab';
    if (!isTabPressed) {
      return;
    }
    if (event.shiftKey) { // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else if (document.activeElement === lastElement) { // Tab
      event.preventDefault();
      firstElement.focus();
    }
  }
}

// Register class
window.brix && window.brix.registerBlock && window.brix.registerBlock('brix-event', BrixModuleEvent.init);
